import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import i18n from '../i18n';
import api from '../api';
import { formatDate } from '../utils/dateUtils';
import '../css/search.css';

const SearchPage = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const lang = i18n.language.includes('-') ? i18n.language.split('-')[0] : i18n.language;

    useEffect(() => {
        const q = searchParams.get('q') || '';
        setQuery(q);

        if (q.trim().length < 2) {
            setResults(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(false);

        api.get(`${process.env.REACT_APP_API_URL}/api/search`, {
            params: { q: q.trim(), lang, limit: 20 },
        })
            .then((response) => {
                if (!cancelled) {
                    setResults(response.data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true);
                    setResults(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [searchParams, lang]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = query.trim();
        if (trimmed.length >= 2) {
            setSearchParams({ q: trimmed });
        }
    };

    const totalCount = results
        ? results.users.length + results.posts.length + results.news.length +
          results.patch_notes.length + results.characters.length
        : 0;

    const renderUser = (u) => (
        <Link key={`user-${u.id}`} to={`/user/${u.id}`} className="search-result">
            <span className="search-result__badge search-result__badge--user">{t('search.user')}</span>
            <span className="search-result__body">
                <span className="search-result__title">{u.username}</span>
                <span className="search-result__meta">{t('search.level')} {u.level}</span>
            </span>
        </Link>
    );

    const renderPost = (p) => (
        <Link key={`post-${p.id}`} to={`/posts/${p.id}`} className="search-result">
            <span className="search-result__badge search-result__badge--post">{t('search.post')}</span>
            <span className="search-result__body">
                <span className="search-result__title search-result__title--clamp">{p.content}</span>
                <span className="search-result__meta">
                    {p.username} · {formatDate(p.created_at, { format: 'LL' })}
                </span>
            </span>
        </Link>
    );

    const renderNews = (n) => (
        <Link key={`news-${n.id}`} to={`/news/${n.id}`} className="search-result">
            <span className="search-result__badge search-result__badge--news">{t('search.news')}</span>
            <span className="search-result__body">
                <span className="search-result__title">{n.title}</span>
                <span className="search-result__meta">{formatDate(n.created_at, { format: 'LL' })}</span>
            </span>
        </Link>
    );

    const renderPatchNote = (p) => (
        <Link key={`patch-${p.id}`} to={`/patch-notes/${p.id}`} className="search-result">
            <span className="search-result__badge search-result__badge--patch">{t('search.patchNote')}</span>
            <span className="search-result__body">
                <span className="search-result__title">{p.title}</span>
                <span className="search-result__meta">
                    {p.version ? `v${p.version} · ` : ''}{formatDate(p.created_at, { format: 'LL' })}
                </span>
            </span>
        </Link>
    );

    const renderCharacter = (c) => (
        <Link key={`char-${c.id}`} to={`/characters?id=${c.id}`} className="search-result">
            <span className="search-result__badge search-result__badge--character">{t('search.character')}</span>
            <span className="search-result__body">
                <span className="search-result__title">{c.name}</span>
            </span>
        </Link>
    );

    const renderSection = (title, items, renderFn) => {
        if (!items || items.length === 0) {
            return null;
        }
        return (
            <section className="search-section">
                <h2 className="search-section__title">{title}</h2>
                <div className="search-section__list">
                    {items.map(renderFn)}
                </div>
            </section>
        );
    };

    return (
        <div className="search-page">
            <div className="search-page__header">
                <h1 className="search-page__title">{t('search.title')}</h1>
                <form onSubmit={handleSubmit} className="search-page__form">
                    <FontAwesomeIcon icon={faSearch} className="search-page__icon" />
                    <input
                        type="text"
                        className="search-page__input"
                        placeholder={t('search.placeholder')}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </form>
            </div>

            {loading && <div className="search-page__empty">{t('search.loading')}</div>}

            {!loading && error && (
                <div className="search-page__empty">{t('search.error')}</div>
            )}

            {!loading && results && totalCount === 0 && (
                <div className="search-page__empty">
                    {t('search.noResultsFor', { query: results.query })}
                </div>
            )}

            {!loading && results && totalCount > 0 && (
                <div className="search-page__sections">
                    {renderSection(t('search.users'), results.users, renderUser)}
                    {renderSection(t('search.posts'), results.posts, renderPost)}
                    {renderSection(t('search.news'), results.news, renderNews)}
                    {renderSection(t('search.patchNotes'), results.patch_notes, renderPatchNote)}
                    {renderSection(t('search.characters'), results.characters, renderCharacter)}
                </div>
            )}
        </div>
    );
};

export default SearchPage;
